import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, signal, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { catchError, finalize, firstValueFrom, of } from 'rxjs';

import type { CentralTransferenciaDto, CorrespondenciaHtmlDto } from '@/core/api/sicom-api/registro/medico/central-transferencia/central-transferencia.model';
import { CentralTransferenciaService } from '@/core/api/sicom-api/registro/medico/central-transferencia/central-transferencia.service';
import { AuthService } from '@/core/auth/auth.service';
import { PermissaoService } from '@/core/api/sicom-api/portal/permissao/permissao.service';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CardContent } from '@/shared/components/card-content/card-content';
import { DateUtils } from '@/shared/utils/date-utils';
import { MenuItem } from 'primeng/api';
import { Badge } from 'primeng/badge';
import { ButtonDirective } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { RadioButton } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { severidadeDataValidade } from '../validade-solicitacao';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type TipoRelatorio = 'certificado-regularidade' | 'oficio-repudio' | 'oficio-confirmacao';

@Component({
    selector: 'app-localizar-por-cpf',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogoPadraoDirective, CommonModule, FormsModule, CardContent, ButtonDirective, Badge, InputText, RadioButton, TableModule, Tag, BotaoAcoes, Dialog],
    templateUrl: './localizar-por-cpf.html',
    styleUrl: './localizar-por-cpf.scss'
})
/** Central de transferência: localização de solicitações por CPF. */
export class LocalizarPorCpfComponent implements OnInit, OnDestroy {
    /** O helper compartilhado, exposto para o template — a regra da cor é uma só na Central. */
    protected readonly severidadeDataValidade = severidadeDataValidade;

    protected readonly EInconPos = EInconPos;
    private readonly centralTransferenciaService = inject(CentralTransferenciaService);
    private readonly permissaoService = inject(PermissaoService);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly authService = inject(AuthService);

    private nomeUsuario = '';

    tipoPesquisa: 'CPF' | 'ID' = 'CPF';
    cpf = '';
    idSolicitacao = '';
    carregando = signal(false);
    pesquisado = false;
    erroConsulta = '';
    resultados: CentralTransferenciaDto[] = [];
    solicitacaoSelecionada: CentralTransferenciaDto | null = null;
    dialogCorrespondenciaVisivel = false;
    dialogCarregando = signal(false);
    dialogErro = '';
    dialogTitulo = '';
    correspondenciasHtml: Array<{ titulo: string; html: string }> = [];
    certificadoPdfUrl: SafeResourceUrl | null = null;
    oficioRepudioPdfUrl: SafeResourceUrl | null = null;
    private _rawPdfUrl: string | null = null;
    private _rawOficioRepudioPdfUrl: string | null = null;
    podeExibirAcoes = signal(false);
    dialogSelecaoVisivel = false;
    dialogSelecaoCarregando = signal(false);
    dialogSelecaoErro = '';
    opcoesDisponiveis: Array<{ tipo: TipoRelatorio; titulo: string; descricao: string; icone: string }> = [];
    relatorioSelecionadoTemp: TipoRelatorio = 'certificado-regularidade';
    private correspondenciaHtmlCache: CorrespondenciaHtmlDto | null = null;
    readonly opcoesRelatorio: Array<{ tipo: TipoRelatorio; titulo: string; descricao: string; icone: string }> = [
        {
            tipo: 'certificado-regularidade',
            titulo: 'Certificado de Regularidade',
            descricao: 'Certifica a regularidade do médico junto ao CRM de origem.',
            icone: 'pi pi-verified'
        },
        {
            tipo: 'oficio-repudio',
            titulo: 'Ofício de Repúdio',
            descricao: 'Ofício formal de repúdio emitido pelo CRM de origem.',
            icone: 'pi pi-ban'
        },
        {
            tipo: 'oficio-confirmacao',
            titulo: 'Ofício de Confirmação',
            descricao: 'Ofício de confirmação da transferência ou inscrição secundária.',
            icone: 'pi pi-check-circle'
        }
    ];
    readonly itensAcoesMenu: MenuItem[] = [
        {
            label: 'Visualizar Correspondência',
            icon: 'pi pi-file',
            styleClass: 'bg-blue-500',
            command: () => this.visualizarCorrespondencia()
        }
    ];

    pesquisar(): void {
        this.erroConsulta = '';
        this.resultados = [];
        this.pesquisado = false;
        this.solicitacaoSelecionada = null;
        this.podeExibirAcoes.set(false);

        if (this.tipoPesquisa === 'CPF') {
            this.pesquisarPorCpf();
            return;
        }

        this.pesquisarPorIdSolicitacao();
    }

    limpar(): void {
        this.cpf = '';
        this.idSolicitacao = '';
        this.resultados = [];
        this.erroConsulta = '';
        this.pesquisado = false;
        this.solicitacaoSelecionada = null;
        this.podeExibirAcoes.set(false);
    }

    alterarTipoPesquisa(tipoPesquisa: 'CPF' | 'ID' | string): void {
        if (tipoPesquisa !== 'CPF' && tipoPesquisa !== 'ID') {
            return;
        }

        if (this.tipoPesquisa === tipoPesquisa) {
            return;
        }

        this.tipoPesquisa = tipoPesquisa;
        this.erroConsulta = '';
        this.pesquisado = false;
        this.resultados = [];
        this.solicitacaoSelecionada = null;
        this.podeExibirAcoes.set(false);
    }

    obterMensagemSemResultado(): string {
        if (this.tipoPesquisa === 'ID') {
            return 'Nenhuma solicitação encontrada para o ID informado.';
        }

        return 'Nenhuma solicitação encontrada para o CPF informado.';
    }

    formatarData(valor?: string | null): string {
        const data = (valor ?? '').trim();
        if (!data) {
            return '-';
        }

        if (DateUtils.isDateString(data)) {
            return DateUtils.formatDate(data);
        }

        return data;
    }

    obterTipoSolicitacao(item: CentralTransferenciaDto): string {
        return this.valorTexto(item.descricaoTipoSolicitacao) || this.valorTexto(item.tipoSolicitacao) || '-';
    }

    obterStatus(item: CentralTransferenciaDto): string {
        return this.valorTexto(item.descricaoStatus) || this.valorTexto(item.status) || '-';
    }

    obterSeveridadeTipo(item: CentralTransferenciaDto): 'info' | 'success' | 'secondary' {
        const tipoCodigo = this.valorTexto(item.tipoSolicitacao).toUpperCase();
        const tipoDescricao = this.valorTexto(item.descricaoTipoSolicitacao).toUpperCase();

        if (tipoCodigo === 'S' || tipoDescricao.includes('SECUND')) {
            return 'success';
        }

        if (tipoCodigo === 'T' || tipoDescricao.includes('TRANSFER')) {
            return 'info';
        }

        return 'secondary';
    }

    formatarUf(uf?: string | null): string {
        const valor = (uf ?? '').trim().toUpperCase();
        return valor || '-';
    }

    obterBandeiraUf(uf?: string | null): string | null {
        const sigla = this.formatarUf(uf);
        if (!/^[A-Z]{2}$/.test(sigla)) {
            return null;
        }

        return `images/estados/${sigla}.png`;
    }

    async onSelecaoAlterada(valor: CentralTransferenciaDto | null): Promise<void> {
        this.solicitacaoSelecionada = valor;
        await this.atualizarPermissaoAcoes();
    }

    visualizarCorrespondencia(): void {
        const idSolicitacao = this.solicitacaoSelecionada?.idSolicitacao;
        if (!idSolicitacao) return;

        this.dialogSelecaoVisivel = true;
        this.dialogSelecaoCarregando.set(true);
        this.dialogSelecaoErro = '';
        this.opcoesDisponiveis = [];
        this.correspondenciaHtmlCache = null;

        this.centralTransferenciaService
            .getCorrespondenciaHtml(idSolicitacao)
            .pipe(
                catchError(() => of(null)),
                finalize(() => {
                    this.dialogSelecaoCarregando.set(false);
                })
            )
            .subscribe({
                next: (html) => {
                    this.correspondenciaHtmlCache = html;
                    this.opcoesDisponiveis = this.filtrarOpcoesDisponiveis(html);
                    if (this.opcoesDisponiveis.length === 0) {
                        this.dialogSelecaoErro = 'Nenhuma correspondência disponível para esta solicitação.';
                    } else {
                        this.relatorioSelecionadoTemp = this.opcoesDisponiveis[0].tipo;
                    }
                },
                error: (erro) => {
                    this.dialogSelecaoErro = this.extrairMensagemErro(erro) ?? 'Não foi possível verificar as correspondências disponíveis.';
                }
            });
    }

    confirmarSelecaoRelatorio(): void {
        this.dialogSelecaoVisivel = false;
        this.abrirDialogCorrespondencia(this.relatorioSelecionadoTemp);
    }

    cancelarDialogSelecao(): void {
        this.dialogSelecaoVisivel = false;
    }

    fecharDialogCorrespondencia(): void {
        this.revogarUrlPdf();
        this.certificadoPdfUrl = null;
        this.oficioRepudioPdfUrl = null;
        this.dialogCorrespondenciaVisivel = false;
        this.dialogCarregando.set(false);
    }

    async ngOnInit(): Promise<void> {
        const user = await this.authService.getUserInfo();
        this.nomeUsuario = user?.name ?? '';
    }

    ngOnDestroy(): void {
        this.revogarUrlPdf();
    }

    private revogarUrlPdf(): void {
        if (this._rawPdfUrl) {
            URL.revokeObjectURL(this._rawPdfUrl);
            this._rawPdfUrl = null;
        }
        if (this._rawOficioRepudioPdfUrl) {
            URL.revokeObjectURL(this._rawOficioRepudioPdfUrl);
            this._rawOficioRepudioPdfUrl = null;
        }
    }

    private async atualizarPermissaoAcoes(): Promise<void> {
        const ufValidacao = this.obterUfValidacaoAcoes();
        if (!ufValidacao) {
            this.podeExibirAcoes.set(false);
            return;
        }

        try {
            const resultado = await firstValueFrom(this.permissaoService.validar({ validacoes: { mesmaUf: ufValidacao } }));
            this.podeExibirAcoes.set(!!(resultado?.validacoes?.['mesmaUf'] ?? false));
        } catch {
            this.podeExibirAcoes.set(false);
        }
    }

    private obterUfValidacaoAcoes(): string | null {
        const ufOrigem = this.valorTexto(this.solicitacaoSelecionada?.ufOrigem).toUpperCase();
        const ufDestino = this.valorTexto(this.solicitacaoSelecionada?.ufDestino).toUpperCase();
        const uf = ufOrigem || ufDestino;

        if (!/^[A-Z]{2}$/.test(uf)) {
            return null;
        }

        return uf;
    }

    private abrirDialogCorrespondencia(tipoRelatorio: TipoRelatorio): void {
        const idSolicitacao = this.solicitacaoSelecionada?.idSolicitacao;
        if (!idSolicitacao) return;

        const opcao = this.opcoesRelatorio.find((o) => o.tipo === tipoRelatorio);
        this.dialogTitulo = `${opcao?.titulo ?? 'Visualizar Correspondência'} - Solicitação ${idSolicitacao}`;
        this.dialogCorrespondenciaVisivel = true;
        this.dialogCarregando.set(true);
        this.dialogErro = '';
        this.correspondenciasHtml = [];
        this.revogarUrlPdf();
        this.certificadoPdfUrl = null;
        this.oficioRepudioPdfUrl = null;

        if (tipoRelatorio === 'certificado-regularidade') {
            this.carregarCertificadoRegularidade(idSolicitacao);
        } else if (tipoRelatorio === 'oficio-repudio') {
            this.carregarOficioRepudio(idSolicitacao);
        } else {
            this.carregarOficioConfirmacao();
        }
    }

    private filtrarOpcoesDisponiveis(html: CorrespondenciaHtmlDto | null): Array<{ tipo: TipoRelatorio; titulo: string; descricao: string; icone: string }> {
        if (!html) return [];
        return this.opcoesRelatorio.filter((opcao) => {
            if (opcao.tipo === 'certificado-regularidade') return !!this.valorTexto(html.certificadoRegularidade);
            if (opcao.tipo === 'oficio-repudio') return !!this.valorTexto(html.oficioRepudio);
            if (opcao.tipo === 'oficio-confirmacao') return !!this.valorTexto(html.oficioConfirmacao);
            return false;
        });
    }

    private carregarCertificadoRegularidade(idSolicitacao: number): void {
        this.centralTransferenciaService
            .getCertificadoRegularidadePdf(idSolicitacao, this.nomeUsuario, this.nomeUsuario)
            .pipe(
                catchError(() => of(null)),
                finalize(() => {
                    this.dialogCarregando.set(false);
                })
            )
            .subscribe({
                next: (pdf) => {
                    if (pdf) {
                        this._rawPdfUrl = URL.createObjectURL(pdf);
                        this.certificadoPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this._rawPdfUrl);
                    } else {
                        const certHtml = this.valorTexto(this.correspondenciaHtmlCache?.certificadoRegularidade);
                        if (certHtml) {
                            this.correspondenciasHtml = [{ titulo: 'Certificado de Regularidade', html: certHtml }];
                        }
                    }
                    if (!this.certificadoPdfUrl && !this.correspondenciasHtml.length) {
                        this.dialogErro = 'Certificado de Regularidade não disponível para esta solicitação.';
                    }
                },
                error: (erro) => {
                    this.dialogErro = this.extrairMensagemErro(erro) ?? 'Não foi possível carregar o Certificado de Regularidade.';
                }
            });
    }

    private carregarOficioRepudio(idSolicitacao: number): void {
        this.centralTransferenciaService
            .getOficioRepudioPdf(idSolicitacao, this.nomeUsuario, this.nomeUsuario)
            .pipe(
                catchError(() => of(null)),
                finalize(() => {
                    this.dialogCarregando.set(false);
                })
            )
            .subscribe({
                next: (pdf) => {
                    if (pdf) {
                        this._rawOficioRepudioPdfUrl = URL.createObjectURL(pdf);
                        this.oficioRepudioPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this._rawOficioRepudioPdfUrl);
                    } else {
                        const repudioHtml = this.valorTexto(this.correspondenciaHtmlCache?.oficioRepudio);
                        if (repudioHtml) {
                            this.correspondenciasHtml = [{ titulo: 'Ofício de Repúdio', html: repudioHtml }];
                        }
                    }
                    if (!this.oficioRepudioPdfUrl && !this.correspondenciasHtml.length) {
                        this.dialogErro = 'Ofício de Repúdio não disponível para esta solicitação.';
                    }
                },
                error: (erro) => {
                    this.dialogErro = this.extrairMensagemErro(erro) ?? 'Não foi possível carregar o Ofício de Repúdio.';
                }
            });
    }

    private carregarOficioConfirmacao(): void {
        const oficioConfirmacao = this.valorTexto(this.correspondenciaHtmlCache?.oficioConfirmacao);
        if (oficioConfirmacao) {
            this.correspondenciasHtml = [{ titulo: 'Ofício de Confirmação', html: oficioConfirmacao }];
        } else {
            this.dialogErro = 'Ofício de Confirmação não disponível para esta solicitação.';
        }
        this.dialogCarregando.set(false);
    }

    private normalizarNumerico(valor: unknown): string {
        return (valor ?? '').toString().replace(/\D+/g, '').trim();
    }

    private pesquisarPorCpf(): void {
        const cpfNormalizado = this.normalizarNumerico(this.cpf);
        if (cpfNormalizado.length !== 11) {
            this.erroConsulta = 'Informe um CPF válido com 11 dígitos.';
            return;
        }

        this.carregando.set(true);
        this.centralTransferenciaService
            .listarPorCpf(cpfNormalizado)
            .pipe(
                finalize(() => {
                    this.carregando.set(false);
                })
            )
            .subscribe({
                next: (response) => {
                    this.resultados = response ?? [];
                    this.pesquisado = true;
                },
                error: (erro) => {
                    this.erroConsulta = this.extrairMensagemErro(erro) ?? 'Não foi possível consultar solicitações por CPF.';
                    this.pesquisado = true;
                }
            });
    }

    private pesquisarPorIdSolicitacao(): void {
        const idNormalizado = this.normalizarNumerico(this.idSolicitacao);
        const idSolicitacao = Number(idNormalizado);

        if (!idNormalizado || !Number.isInteger(idSolicitacao) || idSolicitacao <= 0) {
            this.erroConsulta = 'Informe um ID de solicitação válido.';
            return;
        }

        this.carregando.set(true);
        this.centralTransferenciaService
            .listarPorId(idSolicitacao)
            .pipe(
                finalize(() => {
                    this.carregando.set(false);
                })
            )
            .subscribe({
                next: (response) => {
                    this.resultados = response ? [response] : [];
                    this.pesquisado = true;
                },
                error: (erro) => {
                    this.erroConsulta = this.extrairMensagemErro(erro) ?? 'Não foi possível consultar a solicitação por ID.';
                    this.pesquisado = true;
                }
            });
    }

    private valorTexto(valor: unknown): string {
        return (valor ?? '').toString().trim();
    }

    private extrairMensagemErro(erro: unknown): string | undefined {
        const e = erro as { error?: { message?: string; detail?: string; error?: string }; message?: string; statusText?: string };
        return e?.error?.message || e?.error?.detail || e?.error?.error || e?.message || e?.statusText;
    }
}
